import React from 'react';

/**
 * 16px stroke icon set. Ported from the dashboard handoff so the redesign
 * doesn't need a new dependency. Each glyph is a single inline SVG that
 * inherits `currentColor`, so callers control color with `style.color` or
 * a parent text color.
 *
 * Add new icons by extending the switch. All paths assume a 24×24 viewBox
 * with 1.6px round-capped strokes (matches Lucide visually).
 */
export default function Icon({ name, size = 16, stroke = 1.6, style, ...rest }) {
  const wrap = (children) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
  switch (name) {
    case 'dashboard':
      return wrap(
        <>
          <rect x="3" y="3" width="7" height="9" rx="1.5" />
          <rect x="14" y="3" width="7" height="5" rx="1.5" />
          <rect x="14" y="12" width="7" height="9" rx="1.5" />
          <rect x="3" y="16" width="7" height="5" rx="1.5" />
        </>
      );
    case 'bell':
      return wrap(
        <>
          <path d="M6 8a6 6 0 0 1 12 0c0 4 1.5 5 2 6H4c.5-1 2-2 2-6Z" />
          <path d="M10 19a2 2 0 0 0 4 0" />
        </>
      );
    case 'hardhat':
      return wrap(
        <>
          <path d="M3 17h18v2H3z" />
          <path d="M5 17v-2a7 7 0 0 1 14 0v2" />
          <path d="M10 8V5h4v3" />
        </>
      );
    case 'calendar':
      return wrap(
        <>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 9h18M8 3v4M16 3v4" />
        </>
      );
    case 'wallet':
      return wrap(
        <>
          <path d="M3 7a2 2 0 0 1 2-2h12l4 4v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
          <path d="M16 13h3" />
        </>
      );
    case 'receipt':
      return wrap(
        <>
          <path d="M5 3v18l2-1.5L9 21l2-1.5L13 21l2-1.5L17 21l2-1.5V3" />
          <path d="M8 8h8M8 12h8M8 16h5" />
        </>
      );
    case 'clock':
      return wrap(
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </>
      );
    case 'audit':
      return wrap(
        <>
          <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z" />
          <path d="M14 3v6h6M9 14l2 2 4-4" />
        </>
      );
    case 'team':
      return wrap(
        <>
          <circle cx="9" cy="8" r="3.5" />
          <path d="M2 20c0-3.5 3-6 7-6s7 2.5 7 6" />
          <circle cx="17" cy="6" r="2.5" />
          <path d="M22 17c0-2.5-2-4-5-4" />
        </>
      );
    case 'gear':
      return wrap(
        <>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9c.3.7 1 1.1 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
        </>
      );
    case 'plus':
      return wrap(<path d="M12 5v14M5 12h14" />);
    case 'grid':
      return wrap(
        <>
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </>
      );
    case 'list':
      return wrap(
        <>
          <path d="M8 6h13M8 12h13M8 18h13" />
          <circle cx="4" cy="6" r="1" />
          <circle cx="4" cy="12" r="1" />
          <circle cx="4" cy="18" r="1" />
        </>
      );
    case 'kebab':
      return wrap(
        <>
          <circle cx="12" cy="5" r="1" />
          <circle cx="12" cy="12" r="1" />
          <circle cx="12" cy="19" r="1" />
        </>
      );
    case 'search':
      return wrap(
        <>
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </>
      );
    case 'arrow-up':
      return wrap(<path d="M12 19V5M5 12l7-7 7 7" />);
    case 'arrow-down':
      return wrap(<path d="M12 5v14M5 12l7 7 7-7" />);
    case 'caret':
      return wrap(<path d="m6 9 6 6 6-6" />);
    case 'alert':
      return wrap(
        <>
          <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
          <path d="M12 9v4M12 17h.01" />
        </>
      );
    case 'flag':
      return wrap(<path d="M4 21V4h13l-2 4 2 4H4" />);
    case 'dot':
      return wrap(<circle cx="12" cy="12" r="3" />);
    case 'check':
      return wrap(<path d="m5 12 5 5L20 7" />);
    case 'pulse':
      return wrap(<path d="M3 12h4l3-8 4 16 3-8h4" />);
    case 'trending-up':
      return wrap(
        <>
          <path d="M3 17l6-6 4 4 8-8" />
          <path d="M16 7h5v5" />
        </>
      );
    case 'moon':
      return wrap(<path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />);
    case 'export':
      return wrap(
        <>
          <path d="M12 3v12M7 8l5-5 5 5" />
          <path d="M5 21h14" />
        </>
      );

    /* Schedule-redesign additions */
    case 'share':
      return wrap(
        <>
          <circle cx="6" cy="12" r="2.5" />
          <circle cx="18" cy="6" r="2.5" />
          <circle cx="18" cy="18" r="2.5" />
          <path d="M8.2 10.8 16 7M8.2 13.2 16 17" />
        </>
      );
    case 'filter':
      return wrap(<path d="M3 5h18l-7 9v6l-4-2v-4z" />);
    case 'sort':
      return wrap(
        <>
          <path d="M7 4v16M3 8l4-4 4 4" />
          <path d="M17 20V4M13 16l4 4 4-4" />
        </>
      );
    case 'template':
      return wrap(
        <>
          <rect x="3" y="3" width="18" height="6" rx="1.5" />
          <rect x="3" y="12" width="11" height="9" rx="1.5" />
          <rect x="17" y="12" width="4" height="9" rx="1.5" />
        </>
      );
    case 'upload':
      return wrap(<path d="M12 20V8M6 13l6-5 6 5M5 4h14" />);
    case 'download':
      return wrap(<path d="M12 4v12M6 11l6 5 6-5M5 21h14" />);
    case 'printer':
      return wrap(
        <>
          <path d="M6 8V3h12v5M6 18H4a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-2" />
          <rect x="6" y="14" width="12" height="7" rx="1" />
        </>
      );
    case 'lock':
      return wrap(
        <>
          <rect x="5" y="11" width="14" height="9" rx="1.5" />
          <path d="M8 11V7a4 4 0 0 1 8 0v4" />
        </>
      );
    case 'eye':
      return wrap(
        <>
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
          <circle cx="12" cy="12" r="3" />
        </>
      );
    case 'caret-r':
      return wrap(<path d="m9 6 6 6-6 6" />);
    case 'chevron-up':
      return wrap(<path d="m6 15 6-6 6 6" />);
    /* State-system additions (used by StateScreen) */
    case 'map-pin-off':
      return wrap(
        <>
          <path d="M12.75 7.09a3 3 0 0 1 2.16 2.16" />
          <path d="M17.072 17.072c-1.634 2.17-3.527 3.912-4.34 4.646a1.08 1.08 0 0 1-1.464 0C9.42 20.043 4 14.683 4 10a8 8 0 0 1 1.347-4.434" />
          <path d="M8.41 3.6A8 8 0 0 1 20 10c0 1.92-.91 3.962-2.1 5.9" />
          <path d="m2 2 20 20" />
        </>
      );
    case 'log-in':
      return wrap(
        <>
          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
          <polyline points="10 17 15 12 10 7" />
          <line x1="15" y1="12" x2="3" y2="12" />
        </>
      );
    case 'alert-triangle':
      return wrap(
        <>
          <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </>
      );
    case 'wifi-off':
      return wrap(
        <>
          <path d="M5 12.55a11 11 0 0 1 5.17-2.39" />
          <path d="M16.97 16.97A4 4 0 0 0 12 16a4 4 0 0 0-4 3.4" />
          <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
          <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
          <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
          <line x1="12" y1="20" x2="12.01" y2="20" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </>
      );
    case 'inbox':
      return wrap(
        <>
          <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
          <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
        </>
      );
    case 'wrench':
      return wrap(
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
      );
    case 'sparkles':
      return wrap(
        <>
          <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" />
        </>
      );
    case 'refresh-cw':
      return wrap(
        <>
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </>
      );
    case 'mail':
      return wrap(
        <>
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="m22 7-10 5L2 7" />
        </>
      );
    case 'external-link':
      return wrap(
        <>
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          <polyline points="15 3 21 3 21 9" />
          <line x1="10" y1="14" x2="21" y2="3" />
        </>
      );
    case 'arrow-left':
      return wrap(
        <>
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </>
      );

    /* Grip uses a 16×16 viewBox with filled circles, not strokes,
       so we draw it inline rather than via the wrap() helper. */
    case 'grip':
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 16 16"
          fill="currentColor"
          style={{ display: 'block', ...style }}
          aria-hidden="true"
          {...rest}
        >
          <circle cx="6" cy="4" r="1.1" />
          <circle cx="10" cy="4" r="1.1" />
          <circle cx="6" cy="8" r="1.1" />
          <circle cx="10" cy="8" r="1.1" />
          <circle cx="6" cy="12" r="1.1" />
          <circle cx="10" cy="12" r="1.1" />
        </svg>
      );

    default:
      return null;
  }
}
